import { SendIcon, Trash2Icon } from 'lucide-react';
import pluralize from 'pluralize';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { Conversations } from '@/modules/messaging/components/conversations/Conversations';
import { DraftMessages } from '@/modules/messaging/components/DraftMessages';
import {
  useMessagingActions,
  useSelectedMessageIds,
} from '@/modules/messaging/messagingStore';
import { useDeleteDraftMessagesMutation } from '@/modules/messaging/mutations/useDeleteDraftMessagesMutation';
import { useSendDraftMessagesMutation } from '@/modules/messaging/mutations/useSendDraftMessagesMutation';
import { Button } from '@/modules/ui/components/button/Button';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import Container from '@/modules/ui/components/container/Container';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/modules/ui/components/tabs/Tabs';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const Messaging = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'conversations';

  const bulkActions =
    tab === 'drafts' ? (
      <PermissionsGate permissions={[permissions.Messaging.Write]}>
        <MessagingBulkActions />
      </PermissionsGate>
    ) : null;

  return (
    <Container className="flex h-[calc(100dvh-116px)] flex-col overflow-hidden pb-0">
      <PageHeader title="Messaging" action={bulkActions} />

      <Tabs
        value={tab}
        onValueChange={(tab) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', tab);
            return next;
          });
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="mb-4">
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="drafts">Drafts</TabsTrigger>
        </TabsList>
        <TabsContent value="conversations" className="min-h-0 flex-1">
          <Conversations />
        </TabsContent>
        <TabsContent value="drafts" className="min-h-0 flex-1">
          <DraftMessages />
        </TabsContent>
      </Tabs>
    </Container>
  );
};

const MessagingBulkActions = () => {
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const sendDraftsMutation = useSendDraftMessagesMutation();
  const deleteDraftsMutation = useDeleteDraftMessagesMutation();

  const selectedMessageIds = useSelectedMessageIds();
  const { clearMessages: clearSelectedMessageIds } = useMessagingActions();
  const selectedCount = selectedMessageIds.length;

  const handleSendMessages = () => {
    sendDraftsMutation.mutate(
      { messageIds: selectedMessageIds },
      {
        onError: (error) => {
          toast.error(
            `${pluralize('message', selectedCount)} ${pluralize('has', selectedCount)} not been sent: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${selectedCount} ${pluralize('message', selectedCount)} ${pluralize('has', selectedCount)} been sent`,
          );
          clearSelectedMessageIds();
          setIsSendDialogOpen(false);
        },
      },
    );
  };

  const handleDeleteMessages = () => {
    deleteDraftsMutation.mutate(
      { messageIds: selectedMessageIds },
      {
        onError: (error) => {
          toast.error(
            `${pluralize('message', selectedCount)} ${pluralize('has', selectedCount)} not been deleted: ${extractErrorMessage(error)}`,
          );
        },
        onSuccess: () => {
          toast.success(
            `${selectedCount} ${pluralize('message', selectedCount)} ${pluralize('has', selectedCount)} been deleted`,
          );
          clearSelectedMessageIds();
          setIsDeleteDialogOpen(false);
        },
      },
    );
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={sendDraftsMutation.isPending}
          disabled={selectedCount === 0}
          onClick={() => setIsSendDialogOpen(true)}
        >
          <SendIcon />
          Send {pluralize('message', selectedCount)} ({selectedCount})
        </Button>
        <Button
          variant="destructive"
          loading={deleteDraftsMutation.isPending}
          disabled={selectedCount === 0}
          onClick={() => setIsDeleteDialogOpen(true)}
        >
          <Trash2Icon />
          Delete {pluralize('message', selectedCount)} ({selectedCount})
        </Button>
      </div>

      <ConfirmDialog
        open={isSendDialogOpen}
        onOpenChange={setIsSendDialogOpen}
        title="Send messages?"
        description={`Are you sure you want to send ${selectedCount} ${pluralize('message', selectedCount)}?`}
        confirmText="Send"
        onConfirm={handleSendMessages}
        onCancel={() => setIsSendDialogOpen(false)}
      />

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDeleteMessages}
        title="Delete Messages?"
        description={`Are you sure you want to delete ${selectedCount} ${pluralize('message', selectedCount)}? This action cannot be undone.`}
      />
    </>
  );
};

export default Messaging;
